import React, { useEffect, useState } from 'react';
import { Header, Footer, Chatbot } from './components';
import { 
  Accueil, 
  Phosphokym,
  Produits, 
  Certification, 
  Devis, 
  Contact 
} from './pages';
import './App.css';
import { translate } from './i18n';

const PhosphokymWebsite = () => {
  const [language, setLanguage] = useState(() => (
    typeof window !== 'undefined' && window.localStorage.getItem('phosphokym-language') === 'en' ? 'en' : 'fr'
  ));
  const [currentPage, setCurrentPage] = useState('accueil');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatbotOpen, setChatbotOpen] = useState(false);

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = language === 'en'
      ? 'Phosphokym | Feed Grade Dicalcium Phosphate Supplier in Tunisia'
      : 'Phosphokym | Fournisseur de phosphate dicalcique DCP en Tunisie';
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.content = language === 'en'
        ? 'Tunisian supplier of Feed Grade dicalcium phosphate (DCP) for animal nutrition. Granular DCP in big bags, ISO 22000 certified.'
        : 'Phosphokym, fournisseur tunisien de phosphate dicalcique (DCP) Feed Grade pour la nutrition animale. DCP granulaire en big bags, certifié ISO 22000.';
    }
    const openGraphTitle = document.querySelector('meta[property="og:title"]');
    if (openGraphTitle) openGraphTitle.content = document.title;
    const openGraphDescription = document.querySelector('meta[property="og:description"]');
    if (openGraphDescription && description) openGraphDescription.content = description.content;
    window.localStorage.setItem('phosphokym-language', language);
  }, [language]);

  // Pages du site
  const pages = [
    { id: 'accueil', label: translate(language, 'Accueil') },
    { id: 'phosphokym', label: translate(language, 'À propos') },
    { id: 'produits', label: translate(language, 'DCP & Spécifications') },
    { id: 'certification', label: translate(language, 'Qualité & Traçabilité') },
    { id: 'devis', label: translate(language, 'Demander un devis') },
    { id: 'contact', label: translate(language, 'Contact') }
  ];

  // Données de Phosphokym
  const usine = {
    nom: 'PHOSPHOKYM',
    description: 'Fournisseur B2B de Phosphate Dicalcique pour la nutrition animale et le secteur agricole',
    adresse: 'Nouvelle Zone Industrielle Djebel oust',
    ville: 'Zaghouane, Tunisie',
    telephone: '+216 70 279 320',
    email: 'contact@phosphokym.com.tn',
    commercial: 'commercial@phosphokym.com.tn',
    fondation: 2015,
    employes: '200+',
    specialite: 'Approvisionnement fiable en matières premières pour l’alimentation animale',
    certifications: ['ISO 22000:2018']
  };

  // Offre DCP de Phosphokym
  const produits = [
    { 
      nom: 'DCP Granulaire', 
      formule: 'DCP · Feed Grade', 
      concentration: 'Format granulaire',
      usage: 'Formulation industrielle et distribution de matières premières',
      description: 'Phosphate dicalcique Feed Grade sous forme granulée, conditionné en big bags pour les fabricants d’aliments et les acheteurs B2B.',
      emballage: 'Big bags',
      applications: [
        'Fabrication d’aliments',
        'Mélanges minéraux',
        'Premix nutritionnels',
        'Distribution agricole'
      ]
    }
  ];
  const localizedProduits = produits.map((product) => ({
    ...product,
    nomFr: product.nom,
    nom: translate(language, product.nom),
    formule: translate(language, product.formule),
    concentration: translate(language, product.concentration),
    usage: translate(language, product.usage),
    description: translate(language, product.description),
    emballage: translate(language, product.emballage),
    applications: product.applications.map((application) => translate(language, application))
  }));

  // Certification ISO 22000
  const certification = {
    nom: 'ISO 22000:2018',
    titre: 'Système de Management de la Qualité',
    description: 'Phosphokym structure son approvisionnement autour de contrôles documentés, de la traçabilité des lots et d’un accompagnement adapté aux exigences des acheteurs professionnels.',
    avantages: [
      'Processus de production optimisés',
      'Contrôle qualité rigoureux à chaque étape',
      'Amélioration continue des performances',
      'Satisfaction client garantie',
      'Traçabilité complète des produits'
    ],
    dateCertification: '19 Mars 2024',
    dateRenouvellement: '18 Mars 2027',
    organisme: 'Documentation qualité disponible sur demande'
  };
  const localizedCertification = {
    ...certification,
    titre: translate(language, certification.titre),
    description: translate(language, certification.description),
    avantages: certification.avantages.map((avantage) => translate(language, avantage)),
    organisme: translate(language, certification.organisme)
  };
  if (language === 'en') {
    const englishDate = (month, day) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, month, day)));
    localizedCertification.dateCertification = englishDate(2, 19);
    localizedCertification.dateRenouvellement = englishDate(2, 18).replace('2024', '2027');
  }

  // Chiffres clés
  const chiffres = [
    { nombre: 'DCP', label: translate(language, 'Matière première principale') },
    { nombre: '4', label: translate(language, 'Secteurs servis') },
    { nombre: 'B2B', label: translate(language, 'Approvisionnement en gros') },
    { nombre: 'COA', label: translate(language, 'Documentation par lot') }
  ];

  // Afficher la page active
  const renderPage = () => {
    switch (currentPage) {
      case 'accueil':
        return <Accueil 
          setCurrentPage={setCurrentPage} 
          usine={usine} 
          produits={localizedProduits}
          chiffres={chiffres}
          certification={localizedCertification}
          language={language}
        />;
      case 'phosphokym':
        return <Phosphokym usine={usine} setCurrentPage={setCurrentPage} language={language} />;
      case 'produits':
        return <Produits produits={localizedProduits} setCurrentPage={setCurrentPage} language={language} />;
      case 'certification':
        return <Certification certification={localizedCertification} setCurrentPage={setCurrentPage} language={language} />;
      case 'devis':
        return <Devis produits={localizedProduits} usine={usine} language={language} />;
      case 'contact':
        return <Contact usine={usine} language={language} />;
      default:
        return <Accueil 
          setCurrentPage={setCurrentPage} 
          usine={usine} 
          produits={localizedProduits}
          chiffres={chiffres}
          certification={localizedCertification}
          language={language}
        />;
    }
  };

  return (
    <div className="app">
      <Header 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        pages={pages}
        language={language}
        setLanguage={setLanguage}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        nomUsine="Phosphokym"
      />
      <main className="main">
        {renderPage()}
      </main>
      <Footer 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        pages={pages} 
        usine={usine}
        certification={certification}
        language={language}
      />
      <Chatbot 
        chatbotOpen={chatbotOpen}
        setChatbotOpen={setChatbotOpen}
        setCurrentPage={setCurrentPage}
        usine={usine}
        language={language}
      />
    </div>
  );
};

// ⚠️ IMPORTANT : Notez bien le nom d'export ici
export default PhosphokymWebsite;